import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accessibility, Type, Eye, Sparkles, Volume2, Snail, BookOpenText, Mic, MicOff, RotateCcw, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useSettings } from "@/context/SettingsContext";
import { useReadAloud } from "@/context/ReadAloudContext";

const ROUTES = {
  home: "/", love: "/love", live: "/live", market: "/market", creators: "/creators",
  properties: "/properties", property: "/properties", homes: "/properties",
  jobs: "/jobs", job: "/jobs", learn: "/learn", learning: "/learn",
  students: "/students", student: "/students", voice: "/your-voice", vote: "/vote",
  safety: "/safety", help: "/safety", privacy: "/privacy", cv: "/cv",
};

const Row = ({ icon: Icon, label, hint, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-3 py-3 border-b border-[var(--pula-border)]">
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-pula-green mt-0.5 shrink-0" />
      <div>
        <p className="font-body font-medium text-pula-ink text-sm">{label}</p>
        {hint && <p className="text-xs text-pula-inksoft font-body">{hint}</p>}
      </div>
    </div>
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </div>
);

export function AccessibilityPanel() {
  const navigate = useNavigate();
  const {
    textScale, bumpText, setTextScale, simpleMode, setSimpleMode,
    plainLanguage, setPlainLanguage, reduceMotion, setReduceMotion, easyRead, setEasyRead,
    lowData, setLowData,
  } = useSettings();
  const readAloud = useReadAloud();
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  const handleCommand = useCallback((text) => {
    const t = text.toLowerCase().trim();
    if (t.includes("read") || t.includes("listen")) { readAloud?.supported && readAloud.toggle(); toast.success("Reading this page aloud."); return; }
    if (t.includes("stop")) { readAloud?.stop?.(); toast.success("Stopped."); return; }
    if (t.includes("bigger") || t.includes("larger")) { bumpText(1); return; }
    if (t.includes("smaller")) { bumpText(-1); return; }
    const key = Object.keys(ROUTES).find((k) => t.includes(k));
    if (key) { navigate(ROUTES[key]); toast.success(`Going to ${key}.`); return; }
    toast.error(`Sorry, I didn't catch a page in "${text}". Try "Go to Jobs".`);
  }, [navigate, readAloud, bumpText]);

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice control isn't supported in this browser. Try Chrome."); return; }
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    const rec = new SR();
    rec.lang = "en-GB"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = (e) => handleCommand(e.results[0][0].transcript);
    rec.onend = () => setListening(false);
    rec.onerror = () => { setListening(false); toast.error("Couldn't hear you — please try again."); };
    recRef.current = rec;
    rec.start(); setListening(true);
    toast.info("Listening… say “Go to Jobs”, “Read this page”, or “Bigger text”.");
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button data-testid="a11y-panel-open"
          className="px-2 py-1 rounded hover:bg-white/10 transition-colors flex items-center gap-1">
          <Accessibility className="w-3.5 h-3.5" /> Access
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="a11y-panel">
        <SheetHeader>
          <SheetTitle className="font-heading flex items-center gap-2"><Accessibility className="w-5 h-5 text-pula-green" /> Accessibility</SheetTitle>
          <SheetDescription>Make Pula comfortable for you. Your choices are saved on this device.</SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {/* Text size */}
          <div className="flex items-center justify-between gap-3 py-3 border-b border-[var(--pula-border)]">
            <div className="flex items-start gap-3">
              <Type className="w-5 h-5 text-pula-green mt-0.5" />
              <div>
                <p className="font-body font-medium text-pula-ink text-sm">Text size</p>
                <p className="text-xs text-pula-inksoft font-body">{Math.round(textScale * 100)}%</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="a11y-panel-text-decrease" onClick={() => bumpText(-1)} className="w-8 h-8 rounded-lg border border-[var(--pula-border)] hover:bg-pula-bg2">−</button>
              <button data-testid="a11y-panel-text-reset" onClick={() => setTextScale(1)} className="w-8 h-8 rounded-lg border border-[var(--pula-border)] hover:bg-pula-bg2 grid place-items-center"><RotateCcw className="w-3.5 h-3.5" /></button>
              <button data-testid="a11y-panel-text-increase" onClick={() => bumpText(1)} className="w-8 h-8 rounded-lg border border-[var(--pula-border)] hover:bg-pula-bg2">+</button>
            </div>
          </div>

          <Row icon={Eye} label="Simple mode" hint="Hide extra decoration, show only essentials" checked={simpleMode} onChange={setSimpleMode} testid="a11y-panel-simple" />
          <Row icon={BookOpenText} label="Easy Read mode" hint="Bigger text and more spacing for calmer reading" checked={easyRead} onChange={setEasyRead} testid="a11y-panel-easyread" />
          <Row icon={Snail} label="Reduce motion" hint="Turn off animations and movement" checked={reduceMotion} onChange={setReduceMotion} testid="a11y-panel-reducemotion" />
          <Row icon={Gauge} label="Low-data mode" hint="Save data — hide big images, shrink photos, stop video" checked={lowData} onChange={setLowData} testid="a11y-panel-lowdata" />
          <Row icon={Sparkles} label="Plain language" hint="Explain terms simply, no jargon" checked={plainLanguage} onChange={setPlainLanguage} testid="a11y-panel-plain" />
          {readAloud?.supported && (
            <Row icon={Volume2} label="Read aloud" hint="Have this page read to you" checked={readAloud.speaking} onChange={() => readAloud.toggle()} testid="a11y-panel-readaloud" />
          )}

          {/* Voice navigation */}
          <div className="mt-4 pula-card p-4 bg-pula-bg2">
            <p className="font-body font-medium text-pula-ink text-sm flex items-center gap-2">
              <Mic className="w-4 h-4 text-pula-green" /> Voice control
            </p>
            <p className="text-xs text-pula-inksoft font-body mt-1">Speak to navigate — e.g. “Go to Jobs”, “Read this page”, “Bigger text”.</p>
            <button data-testid="a11y-panel-voice" onClick={toggleVoice}
              className={`mt-3 inline-flex items-center gap-2 rounded-full px-4 h-10 text-sm font-medium transition-colors ${listening ? "bg-pula-accent text-white" : "bg-pula-green text-white hover:bg-pula-greenhover"}`}>
              {listening ? <><MicOff className="w-4 h-4" /> Stop listening</> : <><Mic className="w-4 h-4" /> Start voice control</>}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
